import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export type TabSize = 'xs' | 'sm' | 'md' | 'lg';

export interface TabOption {
  id: string;
  label: string;
  icon?: IconDefinition;
  customIcon?: React.ReactNode;
}

interface SizeConfig {
  containerPadding: string;
  itemPadding: string;
  gap: string;
  textSize: string;
  iconSize: string;
  minWidth: string;
  outerRadius: string;
  innerRadius: string;
  buttonShadow: string;
  activeTextShadow: string;
  hoverInnerGlow: string;
  hoverGlowRadius: string;
}

const SIZE_CONFIGS: Record<TabSize, SizeConfig> = {
  xs: {
    containerPadding: 'p-1',
    itemPadding: 'px-2 py-1',
    gap: 'gap-1.5',
    textSize: 'text-[11px]',
    iconSize: 'text-[12px]',
    minWidth: '50px',
    outerRadius: 'rounded-full',
    innerRadius: 'rounded-full',
    buttonShadow: 'drop-shadow-sm',
    activeTextShadow: 'drop-shadow-[0_1px_2px_rgba(147,197,253,0.9)]',
    hoverInnerGlow: 'shadow-inner-glow-white-sm',
    hoverGlowRadius: '8px',
  },
  sm: {
    containerPadding: 'p-1',
    itemPadding: 'px-2.5 py-1',
    gap: 'gap-1.5',
    textSize: 'text-[12px]',
    iconSize: 'text-sm',
    minWidth: '56px',
    outerRadius: 'rounded-[1.5rem]',
    innerRadius: 'rounded-[1.25rem]',
    buttonShadow: 'drop-shadow-md',
    activeTextShadow: 'drop-shadow-[0_1px_4px_rgba(147,197,253,0.9)]',
    hoverInnerGlow: 'shadow-inner-glow-white-md',
    hoverGlowRadius: '12px',
  },
  md: {
    containerPadding: 'p-1.5',
    itemPadding: 'px-4 py-2',
    gap: 'gap-2',
    textSize: 'text-[13px]',
    iconSize: 'text-lg',
    minWidth: '100px',
    outerRadius: 'rounded-[2rem]',
    innerRadius: 'rounded-[1.75rem]',
    buttonShadow: 'drop-shadow-md',
    activeTextShadow: 'drop-shadow-[0_2px_6px_rgba(147,197,253,0.9)]',
    hoverInnerGlow: 'shadow-inner-glow-white-lg',
    hoverGlowRadius: '20px',
  },
  lg: {
    containerPadding: 'p-2',
    itemPadding: 'px-5 py-2.5',
    gap: 'gap-3',
    textSize: 'text-[15px]',
    iconSize: 'text-xl',
    minWidth: '140px',
    outerRadius: 'rounded-[2.5rem]',
    innerRadius: 'rounded-[2.25rem]',
    buttonShadow: 'drop-shadow-lg',
    activeTextShadow: 'drop-shadow-[0_3px_8px_rgba(147,197,253,0.9)]',
    hoverInnerGlow: 'shadow-inner-glow-white-xl',
    hoverGlowRadius: '30px',
  },
};

interface SlidingPillsProps {
  options: TabOption[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
  size?: TabSize;
  tabMinWidth?: string;
  variant?: 'light' | 'dark';
  iconOnly?: boolean;
}

export const SlidingPills: React.FC<SlidingPillsProps> = ({
  options,
  activeId,
  onChange,
  className = '',
  size = 'md',
  tabMinWidth,
  variant = 'light',
  iconOnly = false,
}) => {
  const [hoveredTabId, setHoveredTabId] = useState<string | null>(null);
  const activeIndex = options.findIndex((opt) => opt.id === activeId);
  const isHoveringActive = hoveredTabId === activeId;

  const config = SIZE_CONFIGS[size];
  const activeMinWidth = tabMinWidth || config.minWidth;
  const isDark = variant === 'dark';
  const containerBg = isDark ? 'bg-black/80 shadow-inner-glow shadow-inner-glow-black-600-sm backdrop-blur-sm border-none' : 'bg-white border-gray-100 shadow-sm';

  return (
    <div
      data-ui="sliding-pills"
      data-theme={variant}
      data-size={size}
      className={`${containerBg} ${config.containerPadding} ${config.outerRadius} border inline-flex min-w-0 max-w-full w-full sm:w-auto ${className}`}
      style={{ '--sliding-pill-hover-glow-radius': config.hoverGlowRadius } as React.CSSProperties}
    >
      <div
        data-ui="sliding-pills-track"
        className="relative grid w-full"
        style={{ gridTemplateColumns: `repeat(${options.length}, minmax(var(--pill-min-width, ${activeMinWidth}), 1fr))` }}
      >
        {/* Pastilla deslizante (Sliding pill) */}
        <div
          data-ui="sliding-pills-selection"
          className={`absolute top-0 bottom-0 ${config.innerRadius} transition-all duration-300 ease-out shadow-inner-glow border-0 outline-none ring-0 ${isDark
            ? `bg-blue-600/90 text-white shadow-inner-glow-white-2xl ${config.buttonShadow}`
            : isHoveringActive
              ? 'bg-blue-700 shadow-inner-glow-white-2xl border-none'
              : 'bg-blue-600 shadow-inner-glow-white-2xl border-none'
            }`}
          style={{
            width: `${100 / options.length}%`,
            transform: `translateX(${activeIndex * 100}%)`,
          }}
        />

        {options.map((option) => {
          const isActive = option.id === activeId;
          const isHovered = hoveredTabId === option.id;

          return (
            <div
              data-ui="sliding-pills-option"
              data-option-id={option.id}
              data-selected={isActive ? 'true' : 'false'}
              key={option.id}
              role="button"
              aria-label={option.label}
              aria-pressed={isActive}
              tabIndex={0}
              onClick={() => onChange(option.id)}
              onMouseEnter={() => setHoveredTabId(option.id)}
              onMouseLeave={() => setHoveredTabId(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onChange(option.id);
                }
              }}
              className={`cursor-pointer relative ${isHovered ? 'z-30' : isActive ? 'z-20' : 'z-10'} flex flex-row items-center justify-center ${config.gap} ${config.itemPadding} ${config.innerRadius} transition-all duration-300 border-0 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 ${isActive
                ? isDark
                  ? `text-white`
                  : 'text-white bg-transparent'
                : isHovered
                  ? isDark
                    ? 'text-white bg-white/10 shadow-inner-glow shadow-inner-glow-white-2xl border-none'
                    : `text-white bg-black shadow-inner-glow ${config.hoverInnerGlow} text-shadow-glow-white border-none`
                  : isDark
                    ? 'text-white hover:text-white bg-transparent'
                    : 'text-blue-600 bg-transparent'
                }`}
            >
              {option.icon ? (
                <FontAwesomeIcon
                  icon={option.icon}
                  className={`sliding-pill-icon ${config.iconSize} transition-all duration-300`}
                  style={(isActive || isHovered) && !isDark ? { filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 12px rgba(255, 255, 255, 0.4))' } : {}}
                />
              ) : option.customIcon ? (
                <div data-ui="sliding-pill-custom-icon" className={`sliding-pill-icon ${config.iconSize} transition-all duration-300 flex items-center justify-center`} style={(isActive || isHovered) && !isDark ? { filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 12px rgba(255, 255, 255,0.4))' } : {}}>
                  {option.customIcon}
                </div>
              ) : null}
              {!iconOnly && (
                <span data-ui="sliding-pill-label" className={`${config.textSize} font-bold tracking-wide whitespace-nowrap`}>
                  {option.label}
                </span>
              )}
              {iconOnly && isHovered && option.label && (
                <div data-ui="sliding-pill-tooltip" className="absolute top-full mt-2.5 left-1/2 transform -translate-x-1/2 z-[9999] px-3 py-1.5 rounded-xl bg-black border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.9)] shadow-inner-glow shadow-inner-glow-white-sm text-white text-[11px] font-semibold whitespace-nowrap pointer-events-none animate-in fade-in slide-in-from-top-1 duration-150 flex flex-col items-center">
                  <div data-ui="sliding-pill-tooltip-arrow" className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rotate-45 bg-black border-t border-l border-white/20" />
                  <span className="relative z-10">{option.label}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
